import { isSupportedLocale, } from "../../translations/global";
import { CurrencyCode, isCurrrencyCode, isTranslation, } from "../internationalization";
import { isInstanceOf, isPriceFactors, } from "./aux";
import { isArray, isBoolean, isNumber, isString, } from "./wsi4_typeguards";
function isWsi4Result(d) {
    const typeGuard = {
        type: isString,
        error: isString,
        return: (_arg) => true,
    };
    return isInstanceOf(d, typeGuard);
}
export var RequestType;
(function (RequestType) {
    RequestType["addSimplePart"] = "add-simple-part";
    RequestType["addUserDefinedTube"] = "add-user-defined-tube";
    RequestType["changeNodesData"] = "change-nodes-data";
    RequestType["changeProjectName"] = "change-project-name";
    RequestType["clearGraph"] = "clear-graph";
    RequestType["configChanged"] = "config-changed";
    RequestType["deleteArticle"] = "delete-article";
    RequestType["disassembleArticle"] = "disassemble-article";
    RequestType["filesChanged"] = "files-changed";
    RequestType["graphChanged"] = "graph-changed";
})(RequestType || (RequestType = {}));
function isEditDataConfigurationEntry(t) {
    const typeGuardMap = {
        columnPriority: isNumber,
        rowPriority: isNumber,
        showInfoIcon: isBoolean,
        visible: isBoolean,
    };
    return isInstanceOf(t, typeGuardMap);
}
function isEditDataConfiguration(t) {
    const typeGuardMap = {
        articleExternalDrawingNumber: isEditDataConfigurationEntry,
        articleExternalPartNumber: isEditDataConfigurationEntry,
        articleExternalRevisionNumber: isEditDataConfigurationEntry,
        articleName: isEditDataConfigurationEntry,
        attachments: isEditDataConfigurationEntry,
        comment: isEditDataConfigurationEntry,
        countersinking: isEditDataConfigurationEntry,
        fixedRotations: isEditDataConfigurationEntry,
        flipSideUsage: isEditDataConfigurationEntry,
        materialWithGas: isEditDataConfigurationEntry,
        mechanicalGrinding: isEditDataConfigurationEntry,
        multiplicity: isEditDataConfigurationEntry,
        testReport: isEditDataConfigurationEntry,
        thickness: isEditDataConfigurationEntry,
        threading: isEditDataConfigurationEntry,
        tubeDimX: isEditDataConfigurationEntry,
        tubeCuttingData: isEditDataConfigurationEntry,
        tapping: isEditDataConfigurationEntry,
    };
    return isInstanceOf(t, typeGuardMap);
}
function isFillerElement(t) {
    const typeGuardMap = {
        columnPriority: isNumber,
        rowPriority: isNumber,
    };
    return isInstanceOf(t, typeGuardMap);
}
function isPartCreationConfig(t) {
    const typeGuardMap = {
        rectangleCutout: isBoolean,
        circleCutout: isBoolean,
        sheetMetalProfile: isBoolean,
        userDefinedTube: isBoolean,
        showOnStartUp: isBoolean,
    };
    return isInstanceOf(t, typeGuardMap);
}
function isDisabledSheetMetalForcing(t) {
    const typeGuardMap = {
        tag: (arg) => arg === "disabled",
    };
    return isInstanceOf(t, typeGuardMap);
}
function isEnabledSheetMetalForcing(t) {
    const typeGuardMap = {
        tag: (arg) => arg === "enabled",
        type: (arg) => arg === "request" || arg === "order",
    };
    return isInstanceOf(t, typeGuardMap);
}
function isSheetMetalForcing(t) {
    return isDisabledSheetMetalForcing(t) || isEnabledSheetMetalForcing(t);
}
function isShowMessageMap(t) {
    const typeGuardMap = {
        unknownError: isBoolean,
        constraintContourInBend: isBoolean,
        constraintBendFlangeTooShort: isBoolean,
        backendIssueInvalidBend: isBoolean,
        backendIssueMultipleOuterContours: isBoolean,
        backendIssueNestingFailed: isBoolean,
        backendIssueUndetectedFeatures: isBoolean,
        constraintBendAreasOverlap: isBoolean,
        constraintMaxBendLineNetLengthExceeded: isBoolean,
        constraintMaxDimensionExceeded: isBoolean,
        constraintMaxSheetThicknessExceeded: isBoolean,
        constraintMinContourSizeUndercut: isBoolean,
        constraintNoBendDeductionFound: isBoolean,
        constraintNoBendDieAssigned: isBoolean,
        constraintSheetNotAvailable: isBoolean,
        constraintTubeNotAvailable: isBoolean,
        constraintProfileNotSupported: isBoolean,
        constraintTubeCuttingProcessCompatibility: isBoolean,
        constraintTubeNestingNotAvailable: isBoolean,
        constraintTubeDetectionNotLicensed: isBoolean,
    };
    return isInstanceOf(t, typeGuardMap);
}
export function isConfiguration(t) {
    const typeGuardMap = {
        headerFunction: (a) => typeof a === "function",
        resultFunction: (a) => typeof a === "function",
        updateFunction: (a) => typeof a === "function",
        locale: isSupportedLocale,
        currencyCode: isCurrrencyCode,
        readOnlyModeEnabled: isBoolean,
        partCreationConfig: isPartCreationConfig,
        showWizard: isBoolean,
        translationOverwrites: (a) => isArray(a, isTranslation),
        attachmentMaxFileSize: isNumber,
        engravingSelection: isBoolean,
        sheetMetalPartForcing: isSheetMetalForcing,
        loadGltf: isBoolean,
        priceFactors: isPriceFactors,
        showMessageMap: isShowMessageMap,
        enableAnyRotation: isBoolean,
        editDataConfiguration: isEditDataConfiguration,
        fillerElements: (a) => isArray(a, isFillerElement),
    };
    return isInstanceOf(t, typeGuardMap);
}
export const defaultConfig = {
    headerFunction: (_rq) => { },
    resultFunction: (rq) => {
        const d = JSON.parse(rq.responseText);
        if (!isWsi4Result(d)) {
            throw Error("Wrong type.");
        }
        return {
            finished: true,
            data: d,
        };
    },
    updateFunction: (_vm, _t) => { },
    locale: "de-DE",
    currencyCode: CurrencyCode.EUR,
    priceFactors: {
        generalFactor: 1.,
        taxesFactor: 1.19,
        transparent: false,
    },
    readOnlyModeEnabled: false,
    editDataConfiguration: {
        articleExternalDrawingNumber: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 2,
            columnPriority: 0,
        },
        articleExternalPartNumber: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 1,
            columnPriority: 0,
        },
        articleExternalRevisionNumber: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 3,
            columnPriority: 0,
        },
        articleName: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 0,
            columnPriority: 0,
        },
        attachments: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 8,
            columnPriority: 1,
        },
        comment: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 8,
            columnPriority: 0,
        },
        countersinking: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 5,
            columnPriority: 1,
        },
        fixedRotations: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 0,
            columnPriority: 1,
        },
        flipSideUsage: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 7,
            columnPriority: 0,
        },
        materialWithGas: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 6,
            columnPriority: 0,
        },
        mechanicalGrinding: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 1,
            columnPriority: 1,
        },
        multiplicity: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 4,
            columnPriority: 0,
        },
        tapping: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 3,
            columnPriority: 1,
        },
        testReport: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 2,
            columnPriority: 1,
        },
        thickness: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 5,
            columnPriority: 0,
        },
        threading: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 4,
            columnPriority: 1,
        },
        tubeDimX: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 10,
            columnPriority: 0,
        },
        tubeCuttingData: {
            showInfoIcon: false,
            visible: true,
            rowPriority: 11,
            columnPriority: 0,
        },
    },
    fillerElements: [],
    partCreationConfig: {
        rectangleCutout: true,
        circleCutout: true,
        sheetMetalProfile: true,
        userDefinedTube: true,
        showOnStartUp: false,
    },
    showWizard: false,
    translationOverwrites: [],
    attachmentMaxFileSize: 4000000,
    engravingSelection: true,
    sheetMetalPartForcing: {
        tag: "disabled",
    },
    loadGltf: true,
    showMessageMap: {
        unknownError: false,
        constraintContourInBend: false,
        constraintBendFlangeTooShort: false,
        backendIssueInvalidBend: false,
        backendIssueMultipleOuterContours: false,
        backendIssueNestingFailed: false,
        backendIssueUndetectedFeatures: false,
        constraintBendAreasOverlap: false,
        constraintMaxBendLineNetLengthExceeded: false,
        constraintMaxDimensionExceeded: false,
        constraintMaxSheetThicknessExceeded: false,
        constraintMinContourSizeUndercut: false,
        constraintNoBendDeductionFound: false,
        constraintNoBendDieAssigned: false,
        constraintSheetNotAvailable: false,
        constraintTubeNotAvailable: false,
        constraintProfileNotSupported: false,
        constraintTubeCuttingProcessCompatibility: false,
        constraintTubeNestingNotAvailable: false,
        constraintTubeDetectionNotLicensed: false,
    },
    enableAnyRotation: true,
};
